import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { BiStoreAlt } from 'react-icons/bi';
import { BsChevronLeft } from 'react-icons/bs';
import { RiUser3Fill } from 'react-icons/ri';
import { RouteComponentProps, withRouter } from 'react-router';

import apiUtil from 'src/api/verifyOtp';
import BludotLogo from 'src/assets/images/open-america-rewards-logo.png';
import Loader from 'src/components/Loader';
import Logo from 'src/components/Logo';
import { VERIFY_OTP } from 'src/containers/Core/Routes';
import OtpVerificationForm from 'src/containers/VerifyOtp/OtpVerificationForm';

import './style.scss';

const VerifyOtp: React.FC<RouteComponentProps> = ({ location, history }) => {
  const [showOtpForm, setShowOtpForm] = useState<boolean>(true);
  const [isInvalidCode, setIsInvalidCode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [transactionResult, setTransactionResult] = useState<VerifyOtpResponse>(null);

  const searchParams = qs.parse(location.search);
  const { otp } = searchParams;

  const verifyCode = async (code: string) => {
    try {
      const data = await apiUtil.verifyCode(code);
      setTransactionResult(data);
      setShowOtpForm(false);
    } catch (e) {
      setIsInvalidCode(true);
      throw e;
    }
  };

  const fetchTransactionDetails = async (code: string) => {
    setLoading(true);
    try {
      await verifyCode(code);
    } catch (error) {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otp) {
      fetchTransactionDetails(otp as string);
    }
  }, [otp]);

  const handleVerify = () => {
    setShowOtpForm(true);
    history.push(VERIFY_OTP);
  };

  if (loading) {
    return <Loader />;
  }

  const renderOtpForm = () => (
    <div className="d-flex mx-auto my-5">
      <div
        className={`otp-verification-wrapper d-flex flex-column
        justify-content-center align-items-center border rounded`}
      >
        <div className="mb-2 text-primary font-size-18 font-weight-bold">Verify code</div>
        <div className="mb-4 font-size-13">Please enter the 6 digit verification code from customer</div>
        <OtpVerificationForm
          verifyCallback={verifyCode}
          initialValue={otp as string}
          isInvalid={isInvalidCode}
          setIsInvalid={setIsInvalidCode}
        />
      </div>
    </div>
  );

  const renderMessage = () => {
    if (!transactionResult) {
      return null;
    }

    const {
      user: { name: userName, profilePicture },
      business: { name: businessName, address, logo: businessLogo },
      amount,
    } = transactionResult;

    return (
      <div className="d-flex flex-column h-100 mx-auto my-5">
        <div className="d-flex justify-content-center images-wrapper position-relative">
          <div className="first-image d-flex flex-column align-items-center w-50 mr-2 text-center position-relative">
            {profilePicture ? (
              <Logo
                variant="rounded-circle"
                imageSrc={atob(profilePicture)}
                className="first-dot mr-2"
                width={64}
                height={64}
              />
            ) : (
              <span className="border rounded-circle text-primary">
                <RiUser3Fill size={64} className="p-3" />
              </span>
            )}
            <span className="font-size-15 text-primary mt-2">{userName}</span>
          </div>

          <div className="arrow" />

          <div className="second-image d-flex flex-column align-items-center w-50 ml-2 text-center position-relative">
            {businessLogo ? (
              <Logo
                variant="rounded-circle"
                imageSrc={atob(businessLogo)}
                className="second-dot mr-2"
                width={64}
                height={64}
              />
            ) : (
              <span className="border rounded-circle text-primary">
                <BiStoreAlt size={64} className="p-3" />
              </span>
            )}
            <span className="font-size-15 text-primary mt-2">{businessName}</span>
            <span className="font-size-13 text-muted mt-2 text-center">
              {`${address.address1}, ${address.address2 || ''} ${address.city}, ${address.state}, ${address.zipCode}`}
            </span>
          </div>
        </div>
        <div
          className={`transaction-currency text-primary text-center d-flex
          justify-content-center align-items-center mt-2`}
        >
          $<span className={`font-weight-bold transaction-amount ml-2`}>{`${amount}`}</span>
        </div>
        <div
          onClick={handleVerify}
          className="d-flex justify-content-center align-items-center text-primary c-pointer font-size-15 my-auto"
        >
          <span className="pb-1 mr-2">
            <BsChevronLeft />
          </span>
          Verify another transaction
        </div>
      </div>
    );
  };

  return (
    <div className="vh-100 d-flex flex-column">
      <div className="d-flex justify-content-center align-items-center p-3 shadow-sm">
        <img width={135} height={40} src={BludotLogo} />
      </div>
      {showOtpForm ? renderOtpForm() : renderMessage()}
      <div className="d-flex justify-content-center p-3 mt-auto small text-muted">
        Copyright © 2021 Bludot Technologies Inc.
      </div>
    </div>
  );
};

export default withRouter(VerifyOtp);
